import { useEffect } from "react";

/**
 * Keyboard for the spreadsheet viewer.
 *
 * The listener sits on the document in the capture phase rather than on a div
 * inside the dialog: MUI leaves focus on the dialog container when a preview
 * opens, so a handler bound inside it never sees Ctrl+Z until something in the
 * grid is clicked. Capture also puts us ahead of MUI's own Escape handling,
 * which is what makes the Escape ladder possible.
 *
 * Previews stack (a CSV inside a ZIP opens a second one), so only the preview
 * whose dialog currently holds focus reacts.
 */

export interface SheetKeyboardHandlers {
  onUndo: () => void;
  onRedo: () => void;
  onCopySelection: () => void;
  onFocusSearch: () => void;
  /** Returns true when it consumed the key and the dialog must stay open. */
  onEscape: () => boolean;
  hasSelection: boolean;
}

function isTextEntry(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null;
  if (!element) return false;
  const tag = element.tagName;
  return (
    tag === "INPUT" || tag === "TEXTAREA" || element.isContentEditable === true
  );
}

/** A MUI menu or popover is open and owns Escape for itself. */
function popoverIsOpen(): boolean {
  return !!document.querySelector(".MuiPopover-root, .MuiMenu-root");
}

export function useSheetKeyboard(
  rootRef: React.RefObject<HTMLElement>,
  handlers: SheetKeyboardHandlers,
): void {
  const {
    onUndo,
    onRedo,
    onCopySelection,
    onFocusSearch,
    onEscape,
    hasSelection,
  } = handlers;

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const root = rootRef.current;
      if (!root) return;

      const dialog = root.closest(".MuiDialog-root");
      const active = document.activeElement;
      if (dialog && active && !dialog.contains(active)) return;

      const ctrl = event.ctrlKey || event.metaKey;
      const key = event.key.toLowerCase();
      const inTextEntry = isTextEntry(event.target);

      if (ctrl && key === "f") {
        event.preventDefault();
        event.stopPropagation();
        onFocusSearch();
        return;
      }
      if (ctrl && !inTextEntry && key === "z") {
        event.preventDefault();
        event.stopPropagation();
        if (event.shiftKey) {
          onRedo();
        } else {
          onUndo();
        }
        return;
      }
      if (ctrl && !inTextEntry && key === "y") {
        event.preventDefault();
        event.stopPropagation();
        onRedo();
        return;
      }
      if (ctrl && !inTextEntry && key === "c" && hasSelection) {
        event.preventDefault();
        onCopySelection();
        return;
      }
      if (event.key === "Escape" && !popoverIsOpen()) {
        if (onEscape()) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
    };

    document.addEventListener("keydown", handler, true);
    return () => document.removeEventListener("keydown", handler, true);
  }, [
    rootRef,
    onUndo,
    onRedo,
    onCopySelection,
    onFocusSearch,
    onEscape,
    hasSelection,
  ]);
}
